import { DataStore } from 'aws-amplify';
import { captureError } from '../common/sentry';
import { AdminActionHistory } from '../models';
import { AdminActionType, AuditLogOptions } from '../pages/admin-panel/utils/types';
import { formatAction, generateChangesSummary } from '../pages/admin-panel/utils/reportingUtils';

/**
 * Logs an admin action to the AdminActionHistory table
 * @param options - The audit log options
 * @returns Promise<AdminActionHistory> - The created audit log entry
 */
export async function logAdminAction(options: AuditLogOptions): Promise<AdminActionHistory> {
  const { actionType, beforeAction, afterAction, actionBy, reason } = options;

  try {
    // Serialize the before and after states
    const beforeActionStr = JSON.stringify(beforeAction, null, 2);
    const afterActionStr = JSON.stringify(
      {
        ...afterAction,
        ...(reason && { reason })
      },
      null,
      2
    );

    const auditLog = await DataStore.save(
      new AdminActionHistory({
        actionType,
        beforeAction: beforeActionStr,
        afterAction: afterActionStr,
        actionBy,
        actionTime: new Date().toISOString()
      })
    );

    console.log('Admin action logged:', {
      id: auditLog.id,
      actionType,
      actionBy,
      actionTime: auditLog.actionTime
    });

    return auditLog;
  } catch (error) {
    console.error('Failed to log admin action:', error);
    captureError(error, 'logAdminAction');
    throw new Error(`Audit logging failed: ${error}`);
  }
}

/**
 * Gets audit logs with optional filters
 * @param filters - Optional filters for querying logs
 * @returns Promise<AdminActionHistory[]> - Array of audit logs
 */
export async function getAuditLogs(filters?: {
  actionType?: AdminActionType;
  actionBy?: string;
  startDate?: Date;
  endDate?: Date;
}): Promise<AdminActionHistory[]> {
  try {
    console.log('getAuditLogs called with filters:', filters);

    let logs: AdminActionHistory[];

    if (filters?.actionType) {
      console.log('Querying with actionType filter:', filters.actionType);
      logs = await DataStore.query(AdminActionHistory, (c) => c.actionType.eq(filters.actionType as string));
    } else {
      console.log('Querying all AdminActionHistory records from DataStore...');
      logs = await DataStore.query(AdminActionHistory);
    }

    console.log('DataStore query returned:', logs.length, 'records');
    console.log('Sample of returned records:', logs.slice(0, 3));

    // Apply additional filters in memory
    let filteredLogs = logs;

    if (filters?.actionBy) {
      filteredLogs = filteredLogs.filter((log) => log.actionBy === filters.actionBy);
    }

    if (filters?.startDate) {
      filteredLogs = filteredLogs.filter((log) => log.actionTime && new Date(log.actionTime) >= filters.startDate!);
    }

    if (filters?.endDate) {
      filteredLogs = filteredLogs.filter((log) => log.actionTime && new Date(log.actionTime) <= filters.endDate!);
    }

    // Sort by most recent first
    return filteredLogs.sort((a, b) => {
      const dateA = a.actionTime ? new Date(a.actionTime).getTime() : 0;
      const dateB = b.actionTime ? new Date(b.actionTime).getTime() : 0;
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Failed to fetch audit logs:', error);
    captureError(error, 'getAuditLogs');
    throw new Error(`Failed to fetch audit logs: ${error}`);
  }
}

/**
 * Formats an audit log entry for display
 * @param log - The audit log entry
 * @returns Formatted audit log object
 */
export function formatAuditLog(log: AdminActionHistory) {
  const isLikelyJsonPayload = (value: string) => {
    const firstChar = value[0];
    return (
      firstChar === '{' ||
      firstChar === '[' ||
      firstChar === '"' ||
      firstChar === '-' ||
      (firstChar >= '0' && firstChar <= '9') ||
      value.startsWith('true') ||
      value.startsWith('false') ||
      value.startsWith('null')
    );
  };

  const parseJsonSafely = (raw: string | null | undefined, fieldName: 'beforeAction' | 'afterAction') => {
    if (!raw) {
      return null;
    }

    const trimmedRaw = raw.trim();
    if (!trimmedRaw) {
      return null;
    }

    // Some older records stored plain text error messages instead of JSON.
    if (!isLikelyJsonPayload(trimmedRaw)) {
      return { _rawText: trimmedRaw, _parseSkipped: true };
    }

    try {
      return JSON.parse(trimmedRaw);
    } catch (error) {
      console.error(`Failed to parse ${fieldName} for audit log ${log.id}:`, error);
      captureError(error, `formatAuditLog-${fieldName}`);
      return { _rawText: trimmedRaw, _parseError: true };
    }
  };

  const beforeAction = parseJsonSafely(log.beforeAction, 'beforeAction');
  const afterActionParsed = parseJsonSafely(log.afterAction, 'afterAction');
  const hasRawData = Boolean(log.beforeAction || log.afterAction);
  const parsedAnyData = beforeAction !== null || afterActionParsed !== null;
  const actionType = formatAction(log.actionType);

  return {
    id: log.id,
    timestamp: log.actionTime,
    user: log.actionBy,
    action: actionType,
    before: beforeAction,
    after: afterActionParsed,
    reason: afterActionParsed?.reason,
    changes: parsedAnyData
      ? generateChangesSummary(beforeAction, afterActionParsed)
      : hasRawData
        ? 'Error parsing log data'
        : 'No changes recorded'
  };
}
